import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Sidebar from '../components/Sidebar'
import {
  useAuditStore,
  type DuplicateDisposition,
  type DuplicateGroup,
  type DuplicateMember,
} from '../stores/auditStore'
import { useLibraryStore } from '../stores/libraryStore'

// ────────────────────────────────────────────────────────────────────────────
// The in-library merge workbench ("Possible duplicates" — the merge half of the
// granularity workbench). DesignGate contract:
// docs/designgate/granularity_workbench_mock.html + ux_decisions "Duplicate
// Review" / "Granularity workbench" rows. Split wires in later once its backend
// (G8 proactive detection) lands; only merge is built here.
//
// "Merge into primary" is ONE action whose effect is classifier-driven and
// rendered in plain words (never the internal enum). A contradiction is not
// mergeable; the dedicated correctness surface is a tracked follow-up
// (ux_decisions "In-library contradiction"), so here it is shown flagged and
// non-mergeable rather than silently dropped.
// ────────────────────────────────────────────────────────────────────────────

type MergeChoice = 'merge' | 'keep'

const REL_PLAIN: Record<string, string> = {
  duplicate: 'the same accomplishment',
  detail: 'the same accomplishment, with an extra detail',
  framing: 'the same accomplishment, phrased differently',
  sibling: 'a different accomplishment in this role',
  new: 'a different accomplishment',
  contradiction: 'the same accomplishment, with a conflicting fact',
}

function effectWords(disp: DuplicateDisposition | undefined): string {
  if (!disp) return 'Merge into the primary.'
  if (disp.adds_detail && disp.adds_perspective) return 'Adds a detail and a perspective.'
  if (disp.adds_detail) return 'Adds a detail.'
  if (disp.adds_perspective) return 'Adds a perspective.'
  return 'Already covered — archived on merge.'
}

function isContradiction(disp: DuplicateDisposition | undefined): boolean {
  return disp?.recommended_action === 'resolve_conflict'
}

// ────────────────────────────────────────────────────────────────────────────
// Neutral description block (Hard Rule 3 §4 — labeled, never CV copy)
// ────────────────────────────────────────────────────────────────────────────

function NeutralDescription({ member }: { member: DuplicateMember }) {
  if (!member.canonical_description) return null
  return (
    <div className="bg-ob-surface border border-ob-secondary/20 border-l-2 border-l-ob-secondary/40 px-3 py-2.5 text-sm text-ob-on-surface rounded-none">
      {member.canonical_description}
      <span className="block mt-1.5 text-xs text-ob-secondary">
        Neutral description — not the text shown on a CV.
      </span>
      {member.description_truncated && (
        <span className="block mt-1 text-xs text-ob-secondary italic">
          Truncated for review — open this proof in your Library to read the full description.
        </span>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Member row
// ────────────────────────────────────────────────────────────────────────────

interface MemberRowProps {
  member: DuplicateMember
  disp: DuplicateDisposition | undefined
  maxSimilarityPct: number
  isPrimary: boolean
  choice: MergeChoice
  canBePrimary: boolean
  menuOpen: boolean
  whyOpen: boolean
  onMakePrimary: () => void
  onChoose: (c: MergeChoice) => void
  onToggleMenu: () => void
  onToggleWhy: () => void
}

function MemberRow({
  member,
  disp,
  maxSimilarityPct,
  isPrimary,
  choice,
  canBePrimary,
  menuOpen,
  whyOpen,
  onMakePrimary,
  onChoose,
  onToggleMenu,
  onToggleWhy,
}: MemberRowProps) {
  const contradiction = isContradiction(disp)
  const recommended: MergeChoice = disp?.recommended_action === 'merge' ? 'merge' : 'keep'

  return (
    <div
      className={`px-5 py-4 border-t border-ob-secondary/20 first:border-t-0 flex flex-col sm:flex-row gap-4 sm:items-start ${
        isPrimary ? 'bg-ob-surface' : ''
      }`}
    >
      {/* Primary picker */}
      <div className="shrink-0 pt-0.5">
        <label
          className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap ${
            isPrimary ? 'text-ob-ink' : 'text-ob-secondary'
          } ${canBePrimary || isPrimary ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
        >
          <input
            type="radio"
            checked={isPrimary}
            disabled={!canBePrimary && !isPrimary}
            onChange={onMakePrimary}
            className="accent-ob-ink w-4 h-4"
          />
          {isPrimary ? 'Primary' : 'Make primary'}
        </label>
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-ob-secondary mb-1.5">
          <span className="font-semibold text-ob-on-surface">{member.employer}</span>
          {member.role_title ? ` · ${member.role_title}` : ''}
          {member.period ? ` · ${member.period}` : ''}
        </p>
        <NeutralDescription member={member} />

        {contradiction ? (
          <div className="mt-3 border border-ob-secondary/40 border-l-2 border-l-ob-accent bg-ob-surface px-3.5 py-3 rounded-none">
            <div className="text-[11px] font-bold uppercase tracking-wider text-ob-accent">
              &#9873; Conflicting fact — not mergeable
            </div>
            {disp?.conflict_description && (
              <p className="mt-1.5 text-xs text-ob-secondary">{disp.conflict_description}</p>
            )}
            <p className="mt-1.5 text-xs text-ob-secondary">
              Fix the figure on either proof in your{' '}
              <Link to="/library" className="text-ob-accent underline underline-offset-2">
                Library
              </Link>{' '}
              — a later review will group them again for a normal merge.
            </p>
          </div>
        ) : (
          !isPrimary && (
            <div className="mt-2">
              <button
                type="button"
                onClick={onToggleWhy}
                className="text-xs font-semibold text-ob-secondary underline underline-offset-2 hover:text-ob-ink"
              >
                Why grouped?
              </button>
              {whyOpen && (
                <div className="mt-2 border border-ob-secondary/20 bg-ob-surface px-3 py-2.5 text-xs text-ob-secondary leading-relaxed rounded-none">
                  <div className="flex justify-between gap-3 py-0.5">
                    <span>Text similarity</span>
                    <span className="font-semibold text-ob-ink">{maxSimilarityPct}%</span>
                  </div>
                  <div className="flex justify-between gap-3 py-0.5">
                    <span>Looks like</span>
                    <span className="font-semibold text-ob-ink text-right">
                      {REL_PLAIN[disp?.relationship ?? ''] ?? 'a possible duplicate'}
                    </span>
                  </div>
                  {typeof disp?.confidence === 'number' && (
                    <div className="flex justify-between gap-3 py-0.5">
                      <span>Confidence</span>
                      <span className="font-semibold text-ob-ink">
                        {Math.round(disp.confidence * 100)}%
                      </span>
                    </div>
                  )}
                  <p className="mt-1.5 text-ob-secondary/80 italic">
                    Fuller match evidence appears here once it&rsquo;s available.
                  </p>
                </div>
              )}
            </div>
          )
        )}
      </div>

      {/* Disposition control (non-primary, mergeable members only) */}
      {!isPrimary && !contradiction && (
        <div className="shrink-0 flex flex-col sm:items-end gap-1.5">
          <div className="relative">
            <button
              type="button"
              onClick={onToggleMenu}
              className={`inline-flex items-center justify-between gap-2 min-w-[210px] border border-ob-secondary/40 bg-ob-surface-card text-xs font-semibold px-3 py-2 rounded-none ${
                choice === 'merge' ? 'text-ob-ink' : 'text-ob-secondary'
              }`}
            >
              <span>{choice === 'merge' ? 'Merge into primary' : "Don't merge"}</span>
              <span className="text-ob-secondary text-[11px]">▾</span>
            </button>
            {menuOpen && (
              <div className="absolute right-0 z-10 mt-1 min-w-[230px] border border-ob-secondary/40 bg-ob-surface-card rounded-none">
                {(['merge', 'keep'] as MergeChoice[]).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => onChoose(opt)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-sm text-left border-b border-ob-secondary/20 last:border-b-0 hover:bg-ob-surface"
                  >
                    <span
                      className={
                        choice === opt
                          ? 'font-semibold text-ob-ink'
                          : opt === 'keep'
                            ? 'text-ob-secondary'
                            : 'text-ob-on-surface'
                      }
                    >
                      {opt === 'merge' ? 'Merge into primary' : "Don't merge (keep separate)"}
                      {recommended === opt && (
                        <span className="text-[10px] font-bold uppercase tracking-wide text-ob-accent">
                          {' '}
                          &middot; Suggested
                        </span>
                      )}
                    </span>
                    <span className={`text-ob-accent font-bold ${choice === opt ? '' : 'invisible'}`}>
                      ✓
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-ob-secondary sm:text-right max-w-[210px] leading-snug">
            {choice === 'merge' ? effectWords(disp) : 'Kept as a separate proof.'}
          </p>
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Group card
// ────────────────────────────────────────────────────────────────────────────

function GroupCard({ group }: { group: DuplicateGroup }) {
  const { applyMerge, resolve } = useAuditStore()
  const { fetchAccomplishments } = useLibraryStore()

  const dispByMember = useMemo(() => {
    const m = new Map<string, DuplicateDisposition>()
    for (const d of group.dispositions) m.set(d.member_id, d)
    return m
  }, [group.dispositions])

  const defaultPrimary = group.survivor_id ?? group.members[0]?.id ?? ''
  const [primaryId, setPrimaryId] = useState(defaultPrimary)
  const [choices, setChoices] = useState<Record<string, MergeChoice>>(() => {
    const init: Record<string, MergeChoice> = {}
    for (const member of group.members) {
      const d = dispByMember.get(member.id)
      init[member.id] = d?.recommended_action === 'merge' ? 'merge' : 'keep'
    }
    return init
  })
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [openWhy, setOpenWhy] = useState<string | null>(null)
  const [applying, setApplying] = useState(false)

  const similarityPct = Math.round(group.max_similarity * 100)

  const mergeMemberIds = group.members
    .filter(
      (m) =>
        m.id !== primaryId &&
        !isContradiction(dispByMember.get(m.id)) &&
        choices[m.id] === 'merge',
    )
    .map((m) => m.id)

  const conflictCount = group.members.filter((m) => isContradiction(dispByMember.get(m.id))).length
  const keptCount = group.members.length - 1 - mergeMemberIds.length

  const handleMakePrimary = (memberId: string) => {
    setPrimaryId(memberId)
    // A proof set to "Don't merge" can't be the primary — selecting it as primary
    // clears that state so the two are never both held.
    setChoices((prev) => ({ ...prev, [memberId]: 'merge' }))
  }

  const handleChoose = (memberId: string, choice: MergeChoice) => {
    setChoices((prev) => ({ ...prev, [memberId]: choice }))
    setOpenMenu(null)
  }

  const handleApply = async () => {
    if (mergeMemberIds.length === 0) return
    setApplying(true)
    const ok = await applyMerge(group.group_id, primaryId, mergeMemberIds)
    if (ok) void fetchAccomplishments()
    setApplying(false)
  }

  const handleKeepAll = async () => {
    setApplying(true)
    await resolve('keep_all', group.group_id)
    setApplying(false)
  }

  const usingDifferentPrimary = primaryId !== group.survivor_id && group.survivor_id !== null

  return (
    <section className="bg-ob-surface-card border border-ob-secondary/40 rounded-none">
      <div className="px-5 py-3.5 border-b border-ob-secondary/20 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-ob-secondary border border-ob-secondary/20 bg-ob-surface px-2.5 py-1">
          {similarityPct}% similar
        </span>
        {group.suggested_action === 'merge' ? (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-ob-accent border border-ob-accent/35 bg-ob-accent/5 px-2.5 py-1">
            Suggested: merge
          </span>
        ) : (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-ob-secondary border border-ob-secondary/20 bg-ob-surface px-2.5 py-1">
            Needs a decision
          </span>
        )}
      </div>

      {/* One disabled fieldset freezes every control while an apply is in flight. */}
      <fieldset disabled={applying} className="border-0 p-0 m-0 disabled:opacity-70">
        {group.members.map((member) => (
          <MemberRow
            key={member.id}
            member={member}
            disp={dispByMember.get(member.id)}
            maxSimilarityPct={similarityPct}
            isPrimary={member.id === primaryId}
            choice={choices[member.id] ?? 'keep'}
            canBePrimary={
              !isContradiction(dispByMember.get(member.id)) && choices[member.id] !== 'keep'
            }
            menuOpen={openMenu === member.id}
            whyOpen={openWhy === member.id}
            onMakePrimary={() => handleMakePrimary(member.id)}
            onChoose={(c) => handleChoose(member.id, c)}
            onToggleMenu={() => setOpenMenu((prev) => (prev === member.id ? null : member.id))}
            onToggleWhy={() => setOpenWhy((prev) => (prev === member.id ? null : member.id))}
          />
        ))}

        <div className="border-t border-ob-secondary/20 px-5 py-3.5 flex flex-wrap justify-between items-center gap-3">
          <span className="text-xs text-ob-secondary mr-auto">
            {mergeMemberIds.length > 0
              ? `Will merge ${mergeMemberIds.length} · keep ${keptCount} separate.`
              : 'Nothing selected to merge.'}
            {conflictCount > 0 && ` ${conflictCount} conflict to resolve in your Library.`}
            {usingDifferentPrimary && mergeMemberIds.length > 0 && (
              <span className="block mt-0.5">
                Using a different primary — merges apply as a combined rewrite.
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={handleKeepAll}
            className="text-sm text-ob-secondary hover:text-ob-ink disabled:opacity-40"
          >
            Keep all separate
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={mergeMemberIds.length === 0}
            className="bg-ob-ink text-ob-on-ink px-5 py-2.5 text-sm font-semibold rounded-none disabled:opacity-40 hover:bg-ob-ink/90"
          >
            {applying ? 'Applying…' : 'Apply merge'}
          </button>
        </div>
      </fieldset>
    </section>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export default function AuditPage() {
  const { groups, loading, error, fetchGroups, reset } = useAuditStore()

  useEffect(() => {
    reset()
    void fetchGroups()
  }, [reset, fetchGroups])

  return (
    <div className="min-h-screen bg-ob-surface flex font-body text-ob-on-surface">
      <Sidebar />

      <main className="flex-1 ml-16 lg:ml-64 px-6 py-8 max-w-3xl w-full">
        <div className="mb-5">
          <Link
            to="/library"
            className="text-xs text-ob-secondary hover:text-ob-ink transition-colors"
          >
            ← Library
          </Link>
        </div>

        <h1 className="font-serif text-2xl text-ob-ink">Duplicate Review</h1>
        <p className="text-sm text-ob-secondary mt-1.5 max-w-xl leading-relaxed">
          Proofs that look like the same accomplishment. For each group, pick which to keep as the
          primary and fold the rest in — or keep them apart. Nothing changes until you apply.
        </p>

        {loading && groups.length === 0 && (
          <div className="flex items-center gap-3 py-16 text-ob-secondary">
            <span className="animate-spin inline-block w-5 h-5 border-2 border-ob-secondary/30 border-t-ob-ink rounded-full" />
            <span className="text-sm">Scanning your library…</span>
          </div>
        )}

        {error && (
          <p className="text-xs text-ob-error border border-ob-error/30 bg-ob-error/5 px-3 py-2 mt-4 rounded-none">
            {error}
          </p>
        )}

        {!loading && !error && groups.length === 0 && (
          <div className="mt-6 border border-dashed border-ob-secondary/40 bg-ob-surface-card px-5 py-10 text-center rounded-none">
            <p className="font-serif text-lg text-ob-ink">No possible duplicates right now.</p>
            <p className="text-sm text-ob-secondary mt-1.5">
              Nothing looks like the same accomplishment twice. We&rsquo;ll flag new ones here after
              your next upload or edit.
            </p>
          </div>
        )}

        {groups.length > 0 && (
          <>
            <p className="text-xs font-semibold uppercase tracking-wider text-ob-secondary mt-7 mb-1">
              {groups.length} possible duplicate group{groups.length !== 1 ? 's' : ''}
            </p>
            <div className="space-y-4 mt-3">
              {groups.map((group) => (
                <GroupCard key={group.group_id} group={group} />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  )
}
